import { useEffect, useState } from 'react'
import { Icon } from '@iconify/react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate, useParams } from 'react-router-dom'
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import { Button } from '@/components/ui/button.jsx'
import { Input } from '@/components/ui/input.jsx'
import { TypographySmall } from '@/components/ui/typography.jsx'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu.jsx'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip.jsx'
import { ApproveDeleteDialog } from '@/components/dialogs/ApproveDeleteDialog.jsx'

import { RoleGuard } from '@/guards/RoleGuard.jsx'
import { EntitlementsKeys } from '@/guards/EntitlementsGuard.jsx'
import { UserRoles } from '@/consts/index.js'
import { useViewsListContext } from '@/context-state/ViewsListContext.jsx'
import { NavItemIcon } from '@/layouts/dashboard/nav/subnavs/NavItemIcon.jsx'
import { usePersistedState } from '@/hooks/usePersistedState.jsx'
import { useFreeTierFence } from '@/hooks/useFreeTierFence.jsx'
import { NewViewDialog } from '@/pages/dashboard/views/NewViewDialog.jsx'
import { AnalyticsService } from '@/services/analytics.js'

// One dashboard row: drag handle + dashboard icon + name (+ rename / duplicate /
// delete). Flat reorderable list, no folders. The icon is clickable to change glyph + color.
const SortableViewItem = ({ view, isActive, onOpen, onRename, onIconChange, onDuplicate, onDelete }) => {

    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: view._id })

    const [isRenaming, setIsRenaming] = useState(false)
    const [draft, setDraft] = useState(view.title || '')

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    }

    const commitRename = () => {
        const next = draft.trim()
        setIsRenaming(false)
        if (next && next !== view.title) {
            onRename(view._id, next)
        } else {
            setDraft(view.title || '')
        }
    }

    const startRename = () => {
        setDraft(view.title || '')
        setIsRenaming(true)
    }

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`flex items-center gap-1 relative rounded-lg overflow-hidden group/view ${isActive ? 'bg-primary/10 text-primary' : 'hover:bg-accent'} ${isDragging ? 'z-50' : ''}`}
        >
            <div
                {...attributes}
                {...listeners}
                className="cursor-grab active:cursor-grabbing p-1"
            >
                <Icon icon="ph:dots-six-vertical" className="h-4 w-4 text-muted-foreground opacity-30 group-hover/view:opacity-100 transition-opacity" />
            </div>

            <NavItemIcon
                icon={view.icon}
                defaultIcon={view.icon?.value || 'lucide:layout-dashboard'}
                defaultClassName="opacity-70"
                onChange={(nextIcon) => onIconChange(view._id, nextIcon)}
            />

            {isRenaming ? (
                <Input
                    autoFocus
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            commitRename()
                        }
                        if (e.key === 'Escape') {
                            setIsRenaming(false)
                            setDraft(view.title || '')
                        }
                    }}
                    onBlur={commitRename}
                    className="h-7 text-sm flex-1 ml-1 mr-2"
                />
            ) : (
                <>
                    <TooltipProvider delayDuration={400}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <div
                                    className="flex-1 min-w-0 cursor-pointer py-1.5 pl-1 pr-1"
                                    onClick={() => onOpen(view._id)}
                                >
                                    <TypographySmall className="font-light truncate block max-w-[110px] !text-[13px]">
                                        {view.title || 'Untitled'}
                                    </TypographySmall>
                                </div>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                                <p>{view.title || 'Untitled'}</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>

                    <DropdownMenu modal={false}>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 opacity-0 group-hover/view:opacity-100 transition-opacity mr-1"
                            >
                                <Icon icon="ph:dots-three-vertical" className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={startRename}>
                                <Icon icon="ph:pencil-simple" className="h-4 w-4 mr-2 flex-shrink-0" />
                                Rename
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => onDuplicate(view._id)}>
                                <Icon icon="ph:copy" className="h-4 w-4 mr-2 flex-shrink-0" />
                                Duplicate
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => onDelete(view)}>
                                <Icon icon="ph:trash" className="h-4 w-4 mr-2 flex-shrink-0" />
                                Delete
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </>
            )}
        </div>
    )
}

// Collapsible "Dashboards" group rendered inside the creative sub-navbar,
// alongside the reports list. Flat, drag-reorderable, no folders.
export const DashboardsNavSection = () => {

    const navigate = useNavigate()
    const { viewId } = useParams()
    const { views, isFetched, isLoading, fetchViews, deleteView, duplicateView, renameView, changeViewIcon, reorderViews } = useViewsListContext()
    const { enforce: enforceCreateView } = useFreeTierFence(EntitlementsKeys.VIEWS_MANAGE, 'free-views-limit', 'views_create')

    const [isOpen, setIsOpen] = usePersistedState('sidebar:dashboards:open', true)
    const [viewToDelete, setViewToDelete] = useState(null)
    const [isNewViewOpen, setIsNewViewOpen] = useState(false)
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))
    // The creative sub-nav shows on every /creative page, so ensure the list is
    // loaded here (ViewLayout only mounts on /creative/views).
    useEffect(() => {
        if (!isFetched && !isLoading) {
            fetchViews()
        }
    }, [isFetched, isLoading])

    // Keep the Dashboards section open when a dashboard is active (navigate / create).
    useEffect(() => {
        if (viewId) {
            setIsOpen(true)
        }
    }, [viewId])

    const openView = (id) => {
        navigate(`creative/views/${id}`)
    }

    const handleCreate = () => {
        if (enforceCreateView()) {
            return
        }
        AnalyticsService.track('view-create-clicked', { from: 'subnav' })
        setIsNewViewOpen(true)
    }

    const handleDuplicate = async (id) => {
        AnalyticsService.track('view-duplicate-clicked', { viewId: id })
        const copy = await duplicateView(id)
        if (copy) {
            openView(copy._id)
        }
    }

    const approveDelete = async () => {
        const target = viewToDelete
        setViewToDelete(null)
        if (!target) {
            return
        }
        await deleteView(target._id)
        if (viewId === target._id) {
            navigate('creative/views')
        }
    }

    const handleDragEnd = (event) => {
        const { active, over } = event
        if (!over || active.id === over.id) {
            return
        }
        const oldIndex = views.findIndex(v => v._id === active.id)
        const newIndex = views.findIndex(v => v._id === over.id)
        if (oldIndex === -1 || newIndex === -1) {
            return
        }
        reorderViews(arrayMove(views, oldIndex, newIndex))
    }

    return (
        <div className="flex-shrink-0">

            <div className="flex items-center justify-between pr-2 mb-1">
                <button
                    onClick={() => setIsOpen(o => !o)}
                    className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-muted-foreground hover:text-foreground transition-colors"
                >
                    <Icon icon="ph:caret-right" className={`h-3 w-3 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
                    Dashboards
                </button>
                <RoleGuard minimumRoleRequired={UserRoles.EDITOR}>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={handleCreate}
                        title="New dashboard"
                    >
                        <Icon icon="ic:baseline-plus" className="h-6 w-6 rounded-full bg-primary/10 p-1 text-primary" />
                    </Button>
                </RoleGuard>
            </div>

            <AnimatePresence initial={false}>
                {isOpen && (
                    <motion.div
                        key="dashboards-list"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                    >
                        <div className="max-h-[35vh] overflow-y-auto pr-2">
                            {(!isFetched && isLoading) ? (
                                <div className="flex items-center justify-center py-4">
                                    <Icon icon="lucide:loader-2" className="h-4 w-4 text-muted-foreground animate-spin" />
                                </div>
                            ) : views.length === 0 ? (
                                <div className="px-2 py-3 text-center">
                                    <TypographySmall className="text-muted-foreground font-light">
                                        No dashboards yet.
                                    </TypographySmall>
                                </div>
                            ) : (
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={handleDragEnd}
                                >
                                    <SortableContext items={views.map(v => v._id)} strategy={verticalListSortingStrategy}>
                                        <div className="flex flex-col gap-0.5">
                                            {views.map((view) => (
                                                <SortableViewItem
                                                    key={view._id}
                                                    view={view}
                                                    isActive={viewId === view._id}
                                                    onOpen={openView}
                                                    onRename={renameView}
                                                    onIconChange={changeViewIcon}
                                                    onDuplicate={handleDuplicate}
                                                    onDelete={setViewToDelete}
                                                />
                                            ))}
                                        </div>
                                    </SortableContext>
                                </DndContext>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <ApproveDeleteDialog
                isDialogOpen={!!viewToDelete}
                onClose={() => setViewToDelete(null)}
                approveDelete={approveDelete}
                title="Delete dashboard"
                subtitle={`Delete "${viewToDelete?.title || 'this dashboard'}"? This can't be undone.`}
            />

            <NewViewDialog isOpen={isNewViewOpen} onClose={() => setIsNewViewOpen(false)} />
        </div>
    )
}
